import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

/**
 * Local UI state for Presidio shell chrome (All drawer / contact / notifications / account).
 * Not NgRx — ephemeral chrome only.
 *
 * Opening one surface closes the others so overlays stay exclusive.
 */
@Injectable({ providedIn: 'root' })
export class PresidioShellUiFacade {
  private readonly allDrawerOpenSubject = new BehaviorSubject(false);
  private readonly contactOpenSubject = new BehaviorSubject(false);
  private readonly notificationsOpenSubject = new BehaviorSubject(false);
  private readonly accountOpenSubject = new BehaviorSubject(false);

  readonly allDrawerOpen$ = this.allDrawerOpenSubject.asObservable();
  /** Contact mega-menu open stream for shared chrome consumers. */
  readonly contactOpen$ = this.contactOpenSubject.asObservable();
  /** Header notifications bell panel. */
  readonly notificationsOpen$ = this.notificationsOpenSubject.asObservable();
  /** Compact account dropdown open stream. */
  readonly accountOpen$ = this.accountOpenSubject.asObservable();

  openAllDrawer(): void {
    this.closeContact();
    this.closeNotifications();
    this.closeAccount();
    this.allDrawerOpenSubject.next(true);
  }

  closeAllDrawer(): void {
    this.allDrawerOpenSubject.next(false);
  }

  toggleAllDrawer(): void {
    if (this.allDrawerOpenSubject.value) {
      this.closeAllDrawer();
    } else {
      this.openAllDrawer();
    }
  }

  openContact(): void {
    this.closeAllDrawer();
    this.closeNotifications();
    this.closeAccount();
    this.contactOpenSubject.next(true);
  }

  closeContact(): void {
    this.contactOpenSubject.next(false);
  }

  setContactOpen(open: boolean): void {
    if (open) {
      this.openContact();
    } else {
      this.closeContact();
    }
  }

  openNotifications(): void {
    this.closeAllDrawer();
    this.closeContact();
    this.closeAccount();
    this.notificationsOpenSubject.next(true);
  }

  closeNotifications(): void {
    this.notificationsOpenSubject.next(false);
  }

  setNotificationsOpen(open: boolean): void {
    if (open) {
      this.openNotifications();
    } else {
      this.closeNotifications();
    }
  }

  openAccount(): void {
    this.closeAllDrawer();
    this.closeContact();
    this.closeNotifications();
    this.accountOpenSubject.next(true);
  }

  closeAccount(): void {
    this.accountOpenSubject.next(false);
  }

  setAccountOpen(open: boolean): void {
    if (open) {
      this.openAccount();
    } else {
      this.closeAccount();
    }
  }

  toggleAccount(): void {
    if (this.accountOpenSubject.value) {
      this.closeAccount();
    } else {
      this.openAccount();
    }
  }
}
